
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Search, RefreshCw, Building2, Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Switch
} from "@/components/ui/switch";

interface Studio {
  id: string;
  name: string;
  owner_email: string;
  verified: boolean;
  created_at: string;
  subscription_tier: string;
}

interface StudioData {
  id: string;
  name: string;
  created_at: string;
}

const AdminStudiosTab = () => {
  const [studios, setStudios] = useState<Studio[]>([]);
  const [filteredStudios, setFilteredStudios] = useState<Studio[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedStudio, setSelectedStudio] = useState<Studio | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [verified, setVerified] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchStudios();
  }, []);

  useEffect(() => {
    filterStudios();
  }, [searchQuery, statusFilter, studios]);

  const fetchStudios = async () => {
    setIsLoading(true);
    try {
      // First fetch all studios
      const { data: studioData, error: studioError } = await supabase
        .from('studios')
        .select(`
          id,
          name,
          created_at
        `);

      if (studioError) throw studioError;

      // For each studio, find an associated profile (simulating owner relationship)
      const formattedData: Studio[] = [];
      
      if (studioData) {
        for (const studio of studioData) {
          // Type guard to ensure we're dealing with a valid studio object
          if (!studio || typeof studio !== 'object') continue;
          
          // Safely access properties
          const studioId = 'id' in studio ? String(studio.id) : '';
          const studioName = 'name' in studio ? String(studio.name) : '';
          const studioCreatedAt = 'created_at' in studio ? String(studio.created_at) : '';
          
          // In a real implementation, you would join with appropriate tables
          // For now, we'll mock some data
          formattedData.push({
            id: studioId,
            name: studioName,
            owner_email: 'studio.owner@example.com', // Mock email
            verified: Math.random() > 0.5, // Mock verification status
            created_at: studioCreatedAt,
            subscription_tier: ['free', 'basic', 'professional', 'enterprise'][Math.floor(Math.random() * 4)] // Mock subscription tier
          });
        }
      }

      setStudios(formattedData);
      setFilteredStudios(formattedData);
    } catch (error) {
      console.error('Error fetching studios:', error);
      toast({
        title: "Fehler beim Laden der Studios",
        description: "Die Studio-Daten konnten nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterStudios = () => {
    let result = [...studios];
    
    // Apply search filter
    if (searchQuery) {
      result = result.filter(studio => 
        studio.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        studio.owner_email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter === 'verified') {
      result = result.filter(studio => studio.verified);
    } else if (statusFilter === 'unverified') {
      result = result.filter(studio => !studio.verified);
    }
    
    setFilteredStudios(result);
  };

  const handleOpenDialog = (studio: Studio) => {
    setSelectedStudio(studio);
    setVerified(studio.verified);
    setSubscriptionTier(studio.subscription_tier);
    setIsDialogOpen(true);
  };

  const handleUpdateStudio = async () => {
    if (!selectedStudio) return;
    
    setIsUpdating(true);
    try {
      // In a real implementation, this would update the actual database fields
      // For now, we'll just update our local state since the actual DB doesn't have these fields

      // Mocking a successful update
      setTimeout(() => {
        // Update local state
        setStudios(prev => prev.map(studio => 
          studio.id === selectedStudio.id ? 
          { ...studio, verified: verified, subscription_tier: subscriptionTier } : 
          studio
        ));
        
        toast({
          title: "Studio aktualisiert",
          description: `Die Einstellungen für ${selectedStudio.name} wurden aktualisiert.`
        });
        
        setIsDialogOpen(false);
        setIsUpdating(false);
      }, 500);
    } catch (error) {
      console.error('Error updating studio:', error);
      toast({
        title: "Fehler beim Aktualisieren des Studios",
        description: "Die Studio-Daten konnten nicht aktualisiert werden.",
        variant: "destructive"
      });
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getSubscriptionDisplayName = (tier: string) => {
    switch (tier) {
      case 'free': return 'Kostenlos';
      case 'basic': return 'Basic';
      case 'professional': return 'Professional';
      case 'enterprise': return 'Enterprise';
      default: return tier;
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle>Studio-Verwaltung</CardTitle>
            <CardDescription>
              Verwalte alle Studios auf der Plattform
            </CardDescription>
          </div>
          <Button
            variant="outline" 
            size="sm" 
            onClick={fetchStudios} 
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Aktualisieren
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suche nach Studio oder Inhaber..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={setStatusFilter}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Studios</SelectItem>
                <SelectItem value="verified">Verifiziert</SelectItem>
                <SelectItem value="unverified">Nicht verifiziert</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-chrona-accent" />
            </div>
          ) : filteredStudios.length > 0 ? (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Studio Name</TableHead>
                    <TableHead>Inhaber</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Abo</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudios.map((studio) => (
                    <TableRow key={studio.id}>
                      <TableCell className="font-medium">{studio.name}</TableCell>
                      <TableCell>{studio.owner_email}</TableCell>
                      <TableCell>
                        {studio.verified ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Check className="h-3 w-3 mr-1" />
                            Verifiziert
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <X className="h-3 w-3 mr-1" />
                            Nicht verifiziert
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {getSubscriptionDisplayName(studio.subscription_tier)}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(studio.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleOpenDialog(studio)}
                        >
                          <Building2 className="h-4 w-4 mr-2" />
                          Bearbeiten
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Keine Studios gefunden.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Studio bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedStudio && (
              <>
                <div className="mb-4">
                  <p className="font-medium">{selectedStudio.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Inhaber: {selectedStudio.owner_email}
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <label className="text-sm font-medium">Verifizierungsstatus</label>
                      <p className="text-sm text-muted-foreground">Studio für öffentliche Buchungen freischalten</p>
                    </div>
                    <Switch 
                      checked={verified}
                      onCheckedChange={setVerified}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Abo-Stufe</label>
                    <Select
                      value={subscriptionTier}
                      onValueChange={setSubscriptionTier}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Abo-Stufe auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Kostenlos</SelectItem>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Abbrechen
                    </Button>
                    <Button 
                      onClick={handleUpdateStudio}
                      disabled={isUpdating || 
                        (selectedStudio.verified === verified && 
                         selectedStudio.subscription_tier === subscriptionTier)}
                    >
                      {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Speichern
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStudiosTab;
